import {Button, Select} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Flex} from 'src/design/atoms/Flex';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Title} from 'src/design/atoms/Title';
import {IconPlus} from '@tabler/icons-react';
import React from 'react';
import type {
  NotificationRuleFormFieldPersonnelTarget,
  NotificationRuleMetadata} from 'src/api/ProactApiClient';
import {
  FormFieldTypeEnum,
  NotificationAttributeEnum,
  NotificationRecipientTypeEnum
} from 'src/api/ProactApiClient';
import {RemovableCard} from 'src/design/organisms/RemovableCard';
import {UserGroupCheckboxList} from 'src/app/databound-elements/UserGroupCheckboxList';
import {CheckboxList} from 'src/design/atoms/CheckboxList';
import {SecondaryActionPanel} from 'src/design/molecules/SecondaryActionPanel';
import {useGetFormDefinitionFieldListQuery} from 'src/redux/queries/formDefinition';
import {TargetAttributeOptions} from '../notificationRuleHelpers';

export interface EditNotificationTargetsProps {
  data: NotificationRuleMetadata;
  onDataChange(data: NotificationRuleMetadata): void;
  disabled: boolean;
  validationError?: string;
  onRecipientsChange?: () => void;
  formDefinitionId?: number;
}

const FormFieldPersonnelAttributeOptions = [
  {value: String(NotificationAttributeEnum.Assignee), label: 'Person'},
  {value: String(NotificationAttributeEnum.Supervisor), label: 'Supervisor'},
  {
    value: String(NotificationAttributeEnum.SuperSupervisor),
    label: "Supervisor's Supervisor",
  },
];

const RecipientTypeOptions = [
  {value: String(NotificationRecipientTypeEnum.To), label: 'To'},
  {value: String(NotificationRecipientTypeEnum.Copy), label: 'CC'},
  {value: String(NotificationRecipientTypeEnum.BlindCopy), label: 'BCC'},
];

export const EditNotificationTargets: React.FC<
  EditNotificationTargetsProps
> = ({
  data,
  onDataChange,
  disabled,
  validationError,
  onRecipientsChange,
  formDefinitionId,
}) => {
  const {data: fieldsData} = useGetFormDefinitionFieldListQuery(
    {formDefinitionId: formDefinitionId!, start: 0, length: 1000},
    {skip: !formDefinitionId}
  );

  const personnelFields = React.useMemo(() => {
    if (!fieldsData?.data) return [];
    return fieldsData.data
      .filter(
        f => f.formFieldType === FormFieldTypeEnum.DropdownSinglePersonnel
      )
      .map(f => ({value: f.invariantId, label: f.label}));
  }, [fieldsData]);

  function handleToGroupChange(values: string[]): void {
    onRecipientsChange?.();
    onDataChange({
      ...data,
      toUserGroupIds: values.map(i => Number(i)),
    });
  }

  function handleToAttributeChange(values: string[]): void {
    onRecipientsChange?.();
    onDataChange({
      ...data,
      toUserAttributes: values.map(i => Number(i)),
    });
  }

  function handleAddFormFieldTarget(): void {
    onRecipientsChange?.();
    const newTarget: NotificationRuleFormFieldPersonnelTarget = {
      formFieldInvariantId: '',
      attributes: [NotificationAttributeEnum.Assignee],
      recipientType: NotificationRecipientTypeEnum.To,
    };
    onDataChange({
      ...data,
      formFieldPersonnelTargets: [
        ...(data.formFieldPersonnelTargets ?? []),
        newTarget,
      ],
    });
  }

  function handleRemoveFormFieldTarget(index: number): void {
    onRecipientsChange?.();
    const targets = [...(data.formFieldPersonnelTargets ?? [])];
    targets.splice(index, 1);
    onDataChange({...data, formFieldPersonnelTargets: targets});
  }

  function handleFormFieldTargetChange(
    index: number,
    updated: NotificationRuleFormFieldPersonnelTarget
  ): void {
    onRecipientsChange?.();
    const targets = [...(data.formFieldPersonnelTargets ?? [])];
    targets[index] = updated;
    onDataChange({...data, formFieldPersonnelTargets: targets});
  }

  return (
    <Stack gap="xs">
      <Title order={4}>Notification Targets</Title>
      <Text size="sm" c="dimmed">
        Choose who receives this notification.
      </Text>

      {validationError && (
        <Alert color="red" title="Validation Error">
          {validationError}
        </Alert>
      )}

      <SecondaryActionPanel title="Send To Targets">
        <CheckboxList
          label={'Send To Attribute'}
          values={TargetAttributeOptions}
          selected={data.toUserAttributes?.map(i => String(i)) ?? []}
          onChange={handleToAttributeChange}
          disabled={disabled}
        />
        <UserGroupCheckboxList
          label={'Send To Groups'}
          showNotificationsGroups={true}
          selected={data.toUserGroupIds?.map(i => String(i)) ?? []}
          onChange={handleToGroupChange}
          disabled={disabled}
        />
        <Text size="xs" c="dimmed">
          Only groups with &quot;Notification Group&quot; enabled are shown.
          Each recipient receives a separate email.
        </Text>
      </SecondaryActionPanel>

      {formDefinitionId && (
        <SecondaryActionPanel title="Form Field Targets">
          <Stack gap="xs" style={{width: '100%'}}>
            <Text size="sm" c="dimmed">
              Target recipients based on a person selected in a form field.
            </Text>
            {(data.formFieldPersonnelTargets ?? []).map((target, index) => (
              <RemovableCard
                key={index}
                index={index}
                onDelete={() => handleRemoveFormFieldTarget(index)}
                disabled={disabled}>
                <Flex
                  direction={{base: 'column', lg: 'row'}}
                  gap="md"
                  align={{base: 'stretch', lg: 'flex-start'}}
                  w="100%">
                  <Select
                    label="Personnel Field"
                    placeholder="Select a field"
                    data={personnelFields}
                    value={target.formFieldInvariantId || null}
                    onChange={value =>
                      handleFormFieldTargetChange(index, {
                        ...target,
                        formFieldInvariantId: value ?? '',
                      })
                    }
                    disabled={disabled}
                    style={{flex: 2}}
                  />
                  <CheckboxList
                    label="Attribute Level"
                    values={FormFieldPersonnelAttributeOptions}
                    selected={target.attributes?.map(a => String(a)) ?? []}
                    onChange={values =>
                      handleFormFieldTargetChange(index, {
                        ...target,
                        attributes: values.map(v => Number(v)),
                      })
                    }
                    disabled={disabled}
                  />
                  <Select
                    label="Recipient Type"
                    data={RecipientTypeOptions}
                    value={String(target.recipientType)}
                    onChange={value =>
                      handleFormFieldTargetChange(index, {
                        ...target,
                        recipientType: Number(
                          value
                        ) as NotificationRecipientTypeEnum,
                      })
                    }
                    disabled={disabled}
                    style={{flex: 1}}
                  />
                </Flex>
              </RemovableCard>
            ))}
            {personnelFields.length === 0 && (
              <Text size="sm" c="dimmed">
                No personnel fields available on this form definition.
              </Text>
            )}
            {!disabled && personnelFields.length > 0 && (
              <Button
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={handleAddFormFieldTarget}
                style={{alignSelf: 'flex-start'}}>
                Add Form Field Target
              </Button>
            )}
          </Stack>
        </SecondaryActionPanel>
      )}
    </Stack>
  );
};
